import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import {
  convertScenarioToMotionV2,
  getScenario,
  scenarioVoiceMapQueryKey,
  type MotionV2ConvertDryRun,
} from '../../../../features/scenarios/api';
import { isLegacyMotionScript } from '../../../../features/scenarios/scriptContract';
import {
  mergeVoiceRefreshedScenes,
  motionV2ConvertBlockers,
  motionV2ConvertErrorMessage,
  type MotionV2ConvertErrorBody,
} from '../voiceLines';
import type { Scenario, ScenarioResponse } from '../types';
import styles from '../ScenarioGenerator.module.css';

/**
 * シナリオ情報の「声の台本 (v2) に変換」(旧契約のモーション台本のときだけ・docs/features/brand-cut-motion-graphics-plan.md §10.7.5)。
 *
 * 押すと先に試算 (dry_run) して、シーンごとの分割後の行数・警告 (ナレーションとセリフの両方がある)・変換できない理由を
 * 確認モーダルで見せる。実行すると backend が全シーンを事前検証してから一括で書き (1 シーンでも上限を超えれば何も書かずに 422)、
 * シナリオを取り直す。逆変換は無い。
 */
export function MotionScriptConvertSection({
  scenario,
  setLastResult,
}: {
  scenario: Scenario;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dry, setDry] = useState<MotionV2ConvertDryRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isLegacyMotionScript(scenario)) return null;

  const openPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      setDry(await convertScenarioToMotionV2(scenario.id, true));
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: MotionV2ConvertErrorBody } }).response;
      toast.error(resp?.data?.error || '変換の試算に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const convert = async () => {
    setLoading(true);
    setError(null);
    try {
      await convertScenarioToMotionV2(scenario.id, false);
      const updated = await getScenario(scenario.id);
      const rows = (updated.scenes || []).map((sc) => ({ ...sc, id: sc.id as string }));
      setLastResult((prev) => (prev ? {
        ...prev,
        scenarios: prev.scenarios.map((s) => (s.id !== scenario.id ? s : {
          ...s,
          generation_metadata: (updated.generation_metadata as Record<string, unknown> | undefined) ?? s.generation_metadata,
          scenes: mergeVoiceRefreshedScenes(s.scenes, rows),
        })),
      } : prev));
      void queryClient.invalidateQueries({ queryKey: scenarioVoiceMapQueryKey(scenario.id) });
      toast.success('話者付きの台本 (v2) に変換しました。ナレーションのセクションで話者に声を割り当てて、ナレーションを生成してください');
      setDry(null);
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: MotionV2ConvertErrorBody } }).response;
      setError(motionV2ConvertErrorMessage(resp?.status, resp?.data));
    } finally {
      setLoading(false);
    }
  };

  const blockers = dry ? motionV2ConvertBlockers(dry) : [];
  const convertible = !!dry && dry.convertible && blockers.length === 0;

  return (
    <div className={styles.scenarioInfoField} data-testid="motion-script-convert-section">
      <label className={styles.scenarioInfoLabel}>台本の形式</label>
      <span style={{ fontSize: '0.8rem', color: 'rgba(245, 241, 230, 0.6)' }}>
        旧形式のモーション台本です。話者付きの台本 (v2) に変換すると、ナレーションを話者「ナレーター」の行に移し、
        登場人物ごとに別の声で読み上げられるようになります。
      </span>
      <button
        type="button"
        className="btn-modal-secondary-sm"
        style={{ alignSelf: 'flex-start', marginTop: 6 }}
        onClick={() => void openPreview()}
        disabled={loading}
      >
        {loading && !dry ? '確認中…' : '話者付きの台本 (v2) に変換'}
      </button>
      <ConfirmDialog
        isOpen={!!dry}
        title="話者付きの台本 (v2) に変換"
        variant="info"
        messageAlign="left"
        hideButtons
        closeOnOverlayClick={!loading}
        message={<>各シーンのナレーションを本文を保ったまま「ナレーター」の行にして、セリフの前に置きます。元に戻す操作はありません。</>}
        onConfirm={() => void convert()}
        onCancel={() => { if (!loading) { setDry(null); setError(null); } }}
      >
        {dry && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 20px 16px', fontSize: 13 }} data-testid="motion-convert-preview">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dry.scenes.map((s) => (
                <span key={s.scene_id} data-testid={`motion-convert-scene-${s.order}`}>
                  {`シーン ${s.order ?? '?'}: ナレーション ${s.narration_lines} 行 + セリフ ${s.existing_lines} 行 → ナレーション ${s.lines} 行`}
                </span>
              ))}
            </div>
            {dry.warnings.map((w) => (
              <span key={`${w.code}-${w.scene_id}`} className={styles.motionSyncStaleNotice}>
                {w.message || `シーン ${w.order ?? '?'} はナレーションとセリフの両方があります。ナレーター行をセリフの前に置くので、順序を確認してください`}
              </span>
            ))}
            {blockers.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="motion-convert-blockers">
                <span style={{ color: 'rgba(248, 113, 113, 0.9)' }}>このままでは変換できません (何も変更しません)</span>
                {blockers.map((b) => <span key={b} style={{ color: 'rgba(248, 113, 113, 0.9)' }}>{`・${b}`}</span>)}
              </div>
            )}
            {error && <span style={{ color: 'rgba(248, 113, 113, 0.9)' }} data-testid="motion-convert-error">{error}</span>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-cancel-sm" onClick={() => { setDry(null); setError(null); }} disabled={loading}>キャンセル</button>
              <button type="button" className="btn-modal-sm" onClick={() => void convert()} disabled={loading || !convertible}>
                {loading ? '変換中…' : '変換する'}
              </button>
            </div>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
